import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

interface NavigationItem {
  name: string
  href: string
  icon: string
  badge?: string
  children?: NavigationItem[]
}

const Sidebar: React.FC = () => {
  const location = useLocation()
  const { t } = useTranslation()

  // Auto-expand sections based on current route
  const getInitialExpandedSections = () => {
    const path = location.pathname
    const expanded: string[] = []

    // Auto-expand Worklist if on any workflow or imaging route
    if (path.startsWith('/workflow') || path.startsWith('/imaging/upload') || path.startsWith('/pathology/patients')) {
      expanded.push('worklist')
    }
    // Auto-expand Quản lý Dữ liệu
    if (path.startsWith('/pathology') || path.startsWith('/imaging/library') || path.startsWith('/analytics/templates')) {
      expanded.push('quản lý dữ liệu')
    }
    // Auto-expand Devices
    if (path.startsWith('/devices') || path.startsWith('/scanner')) {
      expanded.push('devices')
    }
    // Auto-expand Administration
    if (path.startsWith('/admin')) {
      expanded.push('administration')
    }

    return expanded
  }

  const [expandedSections, setExpandedSections] = useState<string[]>(getInitialExpandedSections())

  // Update expanded sections when route changes
  useEffect(() => {
    const newExpanded = getInitialExpandedSections()
    setExpandedSections(prev => {
      // Merge with existing manually toggled sections
      const merged = [...new Set([...prev, ...newExpanded])]
      return merged
    })
  }, [location.pathname])

  const toggleSection = (sectionName: string) => {
    setExpandedSections(prev =>
      prev.includes(sectionName)
        ? prev.filter(s => s !== sectionName)
        : [...prev, sectionName]
    )
  }

  const navigation: NavigationItem[] = [
    {
      name: t('menu.dashboard'),
      href: '/dashboard',
      icon: '📊'
    },
    {
      name: 'Theo dõi Quy trình',
      href: '/workflow/tracking',
      icon: '📈'
    },
    {
      name: 'Worklist',
      href: '#',
      icon: '✅',
      children: [
        { name: 'Tiếp nhận', href: '/pathology/patients', icon: '🏥' },
        { name: 'Tải lên hình ảnh', href: '/imaging/upload', icon: '📤' },
        { name: 'Tạo phiếu', href: '/workflow/pathologist', icon: '📝' },
        { name: 'Phiếu kết quả', href: '/workflow/results', icon: '📋' }
      ]
    },
    {
      name: 'Quản lý Dữ liệu',
      href: '#',
      icon: '🗂️',
      children: [
        { name: t('menu.imageLibrary'), href: '/imaging/library', icon: '🖼️' },
        { name: t('menu.patientManagement'), href: '/pathology/patients/manage', icon: '👥' },
        { name: 'Quản lý Ảnh - Bệnh Nhân', href: '/workflow/technician', icon: '🔬' },
        { name: 'Phiếu kết quả', href: '/pathology/reports', icon: '📋' },
        { name: t('menu.templates'), href: '/analytics/templates', icon: '📝' },
        { name: 'Biểu Mẫu Thông Tin Bệnh Nhân', href: '/pathology/patients/form-builder', icon: '📝' }
      ]
    },
    {
      name: 'Báo Cáo',
      href: '/analytics/reports',
      icon: '📊'
    },
    {
      name: t('menu.devices'),
      href: '#',
      icon: '🔧',
      children: [
        { name: t('menu.scanners'), href: '/scanner', icon: '🔍' },
        { name: t('menu.deviceStatus'), href: '/devices/status', icon: '📡' }
      ]
    },
    {
      name: t('menu.administration'),
      href: '#',
      icon: '⚙️',
      children: [
        { name: t('menu.organizations'), href: '/admin/organizations', icon: '🏥' },
        { name: t('menu.userManagement'), href: '/admin/users', icon: '👥' },
        { name: t('menu.rolesPermissions'), href: '/admin/roles', icon: '🛡️' },
        { name: t('menu.departments'), href: '/admin/departments', icon: '🏢' },
        { name: t('menu.hisLisIntegration'), href: '/admin/integration', icon: '🔗' },
        { name: t('menu.systemSettings'), href: '/admin/settings', icon: '⚙️' },
        { name: 'Sao lưu NAS', href: '/admin/backup', icon: '💾' }
      ]
    }
  ]

  const renderNavigationItem = (item: NavigationItem, level = 0) => {
    const isActive = location.pathname === item.href
    const hasChildren = item.children && item.children.length > 0
    const isExpanded = expandedSections.includes(item.name.toLowerCase())

    if (hasChildren) {
      return (
        <li key={item.name}>
          <button
            onClick={() => toggleSection(item.name.toLowerCase())}
            className={`w-full flex items-center justify-between px-3 py-2 text-left rounded-md text-sm font-medium transition-colors ${
              level === 0
                ? 'text-gray-700 hover:bg-gray-100'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center space-x-3">
              <span className={level === 0 ? 'text-lg' : 'text-base'}>{item.icon}</span>
              <span>{item.name}</span>
            </div>
            <svg
              className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
          {isExpanded && item.children && (
            <ul className="mt-1 ml-6 space-y-1">
              {item.children.map(child => renderNavigationItem(child, level + 1))}
            </ul>
          )}
        </li>
      )
    }

    return (
      <li key={item.name}>
        <Link
          to={item.href}
          className={`flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            isActive
              ? 'bg-blue-100 text-blue-700 border-r-2 border-blue-500'
              : level === 0
                ? 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-700'
          }`}
        >
          <div className="flex items-center space-x-3">
            <span className={level === 0 ? 'text-lg' : 'text-base'}>{item.icon}</span>
            <span>{item.name}</span>
          </div>
          {item.badge && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
              {item.badge}
            </span>
          )}
        </Link>
      </li>
    )
  }

  // Get user info from localStorage
  const userStr = localStorage.getItem('user')
  const user = userStr ? JSON.parse(userStr) : null

  // Check if user is admin (system admin or organization admin)
  const isAdmin = user && (user.is_staff || user.is_superuser)

  // Filter navigation based on user role
  const filteredNavigation = navigation.filter(item => {
    // Show Administration only to admins
    if (item.name === t('menu.administration')) {
      return isAdmin
    }
    return true
  })

  return (
    <div className="bg-white w-72 flex-shrink-0 min-h-screen shadow-lg border-r border-gray-200 flex flex-col">
      {/* User Profile Section */}
      {user && (
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <div className="text-2xl">{user.avatar}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {user.name}
              </p>
              <p className="text-xs text-gray-500 truncate">
                {user.role} • {user.username}
              </p>
            </div>
            <div className="flex-shrink-0">
              <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Menu */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <ul className="space-y-2">
          {filteredNavigation.map(item => renderNavigationItem(item))}
        </ul>
      </nav>

      {/* System Status */}
      <div className="p-4 border-t border-gray-200 bg-gray-50">
        <div className="text-xs text-gray-500 space-y-1">
          <div className="flex justify-between">
            <span>System Status:</span>
            <span className="text-green-600 font-medium">Online</span>
          </div>
          <div className="flex justify-between">
            <span>Active Users:</span>
            <span className="font-medium">12</span>
          </div>
          <div className="flex justify-between">
            <span>Version:</span>
            <span className="font-medium">v2.1.0</span>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="p-4 border-t border-gray-200">
        <div className="flex space-x-2">
          <button className="flex-1 px-3 py-2 text-xs font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors">
            🔍 {t('common.search')}
          </button>
          <button className="flex-1 px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors">
            ➕ {t('common.create')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default Sidebar